import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuthContext, useAuthToggleContext } from "../../components/providers/AuthProvider.jsx";
import { SlideCard, BrandButton, TextInput } from "../../components/ui/index.js";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";

function Login() {
    const auth = useAuthContext();
    const { handleLogin } = useAuthToggleContext();
    const location = useLocation();
    const [email, setEmail]       = useState('');
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const requestedDestination = location.state?.from;
    const safeDestination = requestedDestination?.startsWith("/") && !requestedDestination.startsWith("//")
        ? requestedDestination
        : null;

    const submit = (e) => {
        e.preventDefault();
        handleLogin({ email, password }, safeDestination);
    };

    if (auth.isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        );
    }

    if (auth.isAuth) {
        const destination = safeDestination
            || (auth.role === "admin" ? "/admin/general" : auth.role === "comercial" ? "/admin/proyectos" : "/profile/general/");
        return <Navigate to={destination} replace />;
    }

    const formContent = (
        <>
            <div>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">
                    Acceso a tu cuenta
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold dark:text-white text-gray-900">
                    Iniciar sesión
                </h2>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
                <TextInput
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="tu@email.com"
                    leftIcon={<Mail size={16} />}
                />

                <TextInput
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    leftIcon={<Lock size={16} />}
                    rightSlot={
                        <button
                            type="button"
                            onClick={() => setShowPass(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                            tabIndex={-1}
                        >
                            {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    }
                />

                <BrandButton variant="solid" size="md" type="submit" className="w-full justify-center">
                    Entrar
                </BrandButton>
            </form>

            <div className="flex flex-col items-center gap-1">
                <Link to="/forgot-password" className="text-xs text-gray-500 dark:text-gray-400 hover:underline">
                    ¿Olvidaste tu contraseña?
                </Link>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    ¿No tienes cuenta?{' '}
                    <Link to="/register" className="text-brand-red hover:underline font-semibold">
                        Regístrate
                    </Link>
                </p>
            </div>
        </>
    );

    return (
        <div className="flex flex-col md:flex-row h-full w-full">

            {/* ── MÓVIL: formulario arriba + logo abajo ─────────────────────── */}
            <div className="md:hidden flex flex-col h-full w-full">
                <div className="flex-shrink-0 dark:bg-base-300/80 bg-white/85 backdrop-blur-md border-b border-white/10 dark:border-white/5 px-6 py-8 flex flex-col gap-5">
                    {formContent}
                </div>
                <div className="flex-1" />
            </div>

            {/* ── TABLET / DESKTOP: SlideCard flotante a la derecha ─────────── */}
            <div className="hidden md:flex flex-1 items-center justify-end w-full h-full px-8 lg:px-12">
                <SlideCard
                    direction="right"
                    className="glassCard rounded-2xl shadow-xl w-[50%] lg:w-[40%] max-h-[88vh] overflow-y-auto p-8 flex flex-col gap-5"
                >
                    {formContent}
                </SlideCard>
            </div>

        </div>
    );
}

export default Login;
